import { t } from 'i18next';
import { StationReadinessScoreData, ReadinessScoreBreakdown, MissingDataSummary } from './types';
import { COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA } from '@/constants/CompressorImageTagData.const';
import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { MenuType } from '@/store/PIDEditing/PIDEditStore';
import StationStore from '@/store/Station/StationStore';
import PIDEditStore from '@/store/PIDEditing/PIDEditStore';
import MapBuilderStore from '@/store/MapBuilderStore';

export interface StationReadinessScoreProps {
  title?: string;
  buttonText?: string;
  defaultActiveKey?: string | string[];
}

export interface StationMissingDataFrameProps {
  title?: string;
  onAddModelSpec?: (specId: string) => void;
  onAddImages?: (imageId: string) => void;
  onAddSensorData?: (sensorId: string) => void;
  isLoading?: boolean;
  stationData: StationReadinessScoreData;
}

export interface ModelSpec {
  id: string;
  name: string;
  description: string;
}

export interface ModelSpecsMissingProps {
  percentage: number;
  totalComponents: number;
  missingComponents: number;
  specs?: ModelSpec[];
  onAddModelSpec?: (specId: string) => void;
  isLoading?: boolean;
}

export interface MissingImage {
  id: string;
  name: string;
  imageTypes: string[];
}

export interface MissingImagesProps {
  percentage: number;
  totalComponents: number;
  missingComponents: number;
  images?: MissingImage[];
  onAddImages?: (imageId: string) => void;
  isLoading?: boolean;
}

export interface SensorData {
  id: string;
  name: string;
  missingData: string[];
}

export interface SensorImage {
  id: string;
  name: string;
  missingImages: string[];
}

export interface MissingDataAndImagesProps {
  percentage: number;
  totalSensors: number;
  missingSensors: number;
  sensorData?: SensorData[];
  sensorImages?: SensorImage[];
  onAddData?: (sensorId: string) => void;
  onAddImages?: (sensorId: string) => void;
  isLoading?: boolean;
}

export const calculateReadinessBreakdown = (data: StationReadinessScoreData): ReadinessScoreBreakdown => {
  return {
    compressorModelPercentage: data.compressorModelScore,
    compressorImagePercentage: data.compressorImageScore,
    pipeDataPercentage: data.pipeDataScore,
    totalMissingCompressors: data.compressorsWithoutModel.length,
    totalMissingImages: data.compressorsWithMissingImageData.length,
    totalMissingSensorData: data.flowSensorsWithMissingPipeData.length,
  };
};

export const calculateMissingDataSummary = (data: StationReadinessScoreData): MissingDataSummary => {
  return {
    totalCompressors: data.totalNumberOfCompressors,
    compressorsWithoutModel: data.compressorsWithoutModel.length,
    compressorsWithMissingImages: data.compressorsWithMissingImageData.length,
    totalFlowSensors: data.totalNumberOfFlowSensors,
    flowSensorsWithMissingData: data.flowSensorsWithMissingPipeData.length,
  };
};

export const getCompressorModelMissingData = (data: StationReadinessScoreData) => {
  const summary = calculateMissingDataSummary(data);
  return {
    percentage: data.compressorModelScore,
    totalComponents: summary.totalCompressors,
    missingComponents: summary.compressorsWithoutModel,
    devices: data.compressorsWithoutModel,
  };
};

export const getCompressorMissingImagesData = (data: StationReadinessScoreData) => {
  const summary = calculateMissingDataSummary(data);
  return {
    percentage: data.compressorImageScore,
    totalComponents: summary.totalCompressors,
    missingComponents: summary.compressorsWithMissingImages,
    devices: data.compressorsWithMissingImageData,
  };
};

export const getFlowSensorMissingData = (data: StationReadinessScoreData) => {
  const summary = calculateMissingDataSummary(data);
  return {
    percentage: data.pipeDataScore,
    totalSensors: summary.totalFlowSensors,
    missingSensors: summary.flowSensorsWithMissingData,
    devices: data.flowSensorsWithMissingPipeData,
  };
};

export const transformCompressorsForModelSpecs = (data: StationReadinessScoreData): ModelSpec[] => {
  return data.compressorsWithoutModel.map(compressor => ({
    id: compressor.deviceId,
    name: compressor.name,
    description: t('Missing specs info'),
  }));
};

export const transformCompressorsForMissingImages = (data: StationReadinessScoreData): MissingImage[] => {
  return data.compressorsWithMissingImageData.map(compressor => ({
    id: compressor.deviceId,
    name: compressor.name,
    imageTypes: compressor.missingTags.map(tag => {
      const tagData = COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[tag];
      return tagData ? tagData.translationKey : tag.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }),
  }));
};

export const transformFlowSensorsForMissingData = (data: StationReadinessScoreData): SensorData[] => {
  return data.flowSensorsWithMissingPipeData
    .filter(sensor => sensor.missingDataFields && sensor.missingDataFields.length > 0)
    .map(sensor => ({
      id: sensor.deviceId,
      name: sensor.name,
      missingData: sensor.missingDataFields.map(field => formatCamelCaseToTitleCase(field)),
    }));
};

export const transformFlowSensorsForMissingImages = (data: StationReadinessScoreData): SensorImage[] => {
  return data.flowSensorsWithMissingPipeData
    .filter(sensor => sensor.missingTags && sensor.missingTags.length > 0)
    .map(sensor => ({
      id: sensor.deviceId,
      name: sensor.name,
      missingImages: (sensor.missingTags || []).map(tag => {
        const tagData = COMPRESSOR_IMAGE_TAG_DIRECTORY_DATA[tag];
        return tagData ? tagData.translationKey : formatKebabCaseToTitleCase(tag);
      }),
    }));
};

export const formatKebabCaseToTitleCase = (kebabCaseString: string): string => {
  return kebabCaseString.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const formatCamelCaseToTitleCase = (camelCaseString: string): string => {
  return camelCaseString
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

export const formatMissingTags = (tags: string[]): string => {
  return tags.map(tag => formatKebabCaseToTitleCase(tag)).join(', ');
};

export const formatMissingFields = (fields: string[]): string => {
  return fields.join(', ');
};

export const shouldShowCompressorBuckets = (data: StationReadinessScoreData): boolean => {
  return data.totalNumberOfCompressors > 0;
};

export const shouldShowModelSpecsBucket = (data: StationReadinessScoreData): boolean => {
  return data.compressorsWithoutModel.length > 0;
};

export const shouldShowMissingImagesBucket = (data: StationReadinessScoreData): boolean => {
  return data.compressorsWithMissingImageData.length > 0;
};

export const shouldShowFlowSensorBucket = (data: StationReadinessScoreData): boolean => {
  return (
    data.totalNumberOfFlowSensors > 0 &&
    data.flowSensorsWithMissingPipeData.some(
      sensor =>
        (sensor.missingDataFields && sensor.missingDataFields.length > 0) ||
        (sensor.missingTags && sensor.missingTags.length > 0),
    )
  );
};

const ASSET_DEVICE_TYPES = [...compressorTypes];

interface MenuPosition {
  x: number;
  y: number;
}

const isAssetDevice = (deviceType: string): boolean => {
  return ASSET_DEVICE_TYPES.includes(deviceType as DeviceType);
};

const ensureEditMode = async (pidEditStore: PIDEditStore): Promise<boolean> => {
  if (pidEditStore.isEditModeEnabled) {
    return true;
  }

  try {
    await pidEditStore.lockStationDraft();
    pidEditStore.toggleEditMode(true);
    pidEditStore.setIsInEditMode(true);
    return true;
  } catch (error) {
    console.error('Failed to enter edit mode:', error);
    return false;
  }
};

const openDeviceDetails = (device: unknown, pidEditStore: PIDEditStore, position?: MenuPosition) => {
  const deviceTyped = device as { deviceType: DeviceType };
  pidEditStore.showMenu(
    MenuType.DeviceDetails,
    position ?? { x: 0, y: 0 },
    { entity: device as never, deviceType: deviceTyped.deviceType, fromStationReadiness: true }
  );
};

export const handleDeviceEdit = async (
  deviceId: string,
  stationStore: StationStore,
  pidEditStore: PIDEditStore,
  mapBuilderStore: MapBuilderStore,
  onActiveKeyChange?: (key: string | string[]) => void,
) => {
  const device = stationStore.getDeviceById(deviceId);
  if (!device) {
    console.warn('Device not found for deviceId:', deviceId);
    return;
  }

  const editModeEnabled = await ensureEditMode(pidEditStore);
  if (!editModeEnabled) return;

  if (isAssetDevice((device as { deviceType: string }).deviceType)) {
    openDeviceDetails(device, pidEditStore);
  } else {
    openDeviceDetails(device, pidEditStore, { x: window.innerWidth / 2 - 112, y: window.innerHeight - 500 });
  }

  onActiveKeyChange?.([]);
};

export const handleAddImages = (
  deviceId: string,
  mapBuilderStore: MapBuilderStore,
  onActiveKeyChange?: (key: string | string[]) => void,
) => {
  mapBuilderStore.selectDeviceById(deviceId, 'assetImages');
  onActiveKeyChange?.([]);
};

